/**
 * Tea-time nudge — the keystone 4pm push.
 *
 * Fires `onFire` at most once per local-date, within the user's stay window,
 * inside a deliberately tight 1-hour catch-up window: 16:00–16:59 local.
 *
 *   - User opens at 14:30 → tick waits until 16:00, then fires.
 *   - User opens at 16:30 and hasn't seen today's nudge → fires immediately.
 *   - User opens at 17:30 and hasn't seen today's nudge → SKIPS entirely.
 *
 * The skip case (post-17:00) is the 14 Jun 2026 premium tightening. The
 * earlier policy treated any post-16:00 open as a valid catch-up moment,
 * which produced "what's for tea?" picks at 8:43pm when guests had
 * already eaten — an intrusive non-sequitur rather than the attentive
 * nudge the keystone is meant to be. Discipline rule: miss the moment
 * cleanly. The window is the moment.
 *
 * Scope is per-device. Multiple devices fire independently. The real
 * implementation will dedupe via server-pushed Web Push notifications keyed
 * by user ID — that work lands with the API repo.
 *
 * v1 storage: a single Zod-validated localStorage entry tracks the most
 * recent local-date the nudge fired. Cleaned up on sign-out via the wider
 * `ti:` namespace wipe (when sign-out lands).
 */
import { useEffect, useRef } from 'react';
import { z } from 'zod';
const STORAGE_KEY = 'ti:teatime';
const TEATIME_HOUR_LOCAL = 16;
/**
 * Latest hour at which catch-up can still fire. If `now.getHours()` is
 * at or beyond this value, the nudge skips entirely for today — the
 * contextual moment for "what's for tea?" has passed and suggesting
 * picks at 8pm reads as intrusive rather than helpful.
 *
 * Set to 17 (5pm) so the firing window is the 16:00 hour only
 * (16:00–16:59 inclusive). One sensible hour, not a sliding scale of
 * "still arguably relevant" — premium discipline says the moment is
 * the moment. Tea decisions get made between 4–5pm in practice;
 * after that, guests are heading out, mid-prep, or already eating.
 *
 * Force-fire mode (`?nudge=now` in preview/dev, see `isForceFireMode`
 * below) bypasses both this check and the early-hour check so manual
 * testing of the picks message format isn't bound by the clock.
 */
const TEATIME_LATEST_HOUR_LOCAL = 17;
const CHECK_INTERVAL_MS = 60_000; // every 60s
const INITIAL_TICK_DELAY_MS = 800; // lets pending UI (e.g. onboarding ack) land first
// ----- Storage -----
const teatimeStateSchema = z.object({
  lastFiredLocalDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
type TeatimeState = z.infer<typeof teatimeStateSchema>;
function readState(): TeatimeState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = teatimeStateSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
function writeState(state: TeatimeState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be disabled, full, or in private mode — fail silent.
  }
}
function clearState(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
// ----- Date helpers -----
/** YYYY-MM-DD in local time (not UTC). */
export function getLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function isWithinStayWindow(
  today: string,
  start: string | undefined,
  end: string | undefined,
): boolean {
  // String compare on YYYY-MM-DD is lexicographically equivalent to date order.
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}
// ----- Force-fire (dev / preview only) -----
function isForceFireMode(): boolean {
  // Hard gate — only production must follow the natural 16:00 rhythm.
  // VITE_APP_ENV is 'production' on the live domain, 'preview' on Cloudflare
  // Pages preview deploys (matches the SW registration gate). MODE alone
  // can't distinguish the two — both build with vite build.
  if (import.meta.env.VITE_APP_ENV === 'production') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('nudge') === 'now';
  } catch {
    return false;
  }
}
// ----- Hook -----
export interface TeatimeNudgeConfig {
  /**
   * True when the user is past onboarding and a nudge would be appropriate.
   * Set false during onboarding or when no profile is loaded.
   */
  readonly enabled: boolean;
  /** Called once per local-date when the trigger fires. */
  readonly onFire: () => void;
  /** Profile stay-window bounds (YYYY-MM-DD). Optional — open-ended if unset. */
  readonly stayStart?: string;
  readonly stayEnd?: string;
}
interface ShouldFireArgs {
  readonly now: Date;
  readonly lastFired: string | null;
  readonly stayStart: string | undefined;
  readonly stayEnd: string | undefined;
  readonly force: boolean;
}
function shouldFire(args: ShouldFireArgs): boolean {
  const today = getLocalDateString(args.now);
  if (args.lastFired === today) return false;
  if (!args.force) {
    const hour = args.now.getHours();
    // Early gate — still in the morning / early afternoon.
    if (hour < TEATIME_HOUR_LOCAL) return false;
    // Late gate (14 Jun 2026) — moment has passed. Catch-up window is
    // a single hour: 16:00–16:59 inclusive. See file header for rationale.
    if (hour >= TEATIME_LATEST_HOUR_LOCAL) return false;
    if (!isWithinStayWindow(today, args.stayStart, args.stayEnd)) return false;
  }
  return true;
}
export function useTeatimeNudge(config: TeatimeNudgeConfig): void {
  // Stable refs for callback + bounds so we don't re-mount the timer on every
  // parent render. Only `enabled` changes the effect lifecycle.
  const onFireRef = useRef(config.onFire);
  onFireRef.current = config.onFire;
  const stayStartRef = useRef(config.stayStart);
  stayStartRef.current = config.stayStart;
  const stayEndRef = useRef(config.stayEnd);
  stayEndRef.current = config.stayEnd;
  const { enabled } = config;
  useEffect(() => {
    if (!enabled) return;
    const force = isForceFireMode();
    if (force) {
      // Allow re-firing on each reload while the param is in the URL.
      clearState();
    }
    const tick = () => {
      const now = new Date();
      const state = readState();
      const lastFired = state?.lastFiredLocalDate ?? null;
      const fireArgs: ShouldFireArgs = {
        now,
        lastFired,
        stayStart: stayStartRef.current,
        stayEnd: stayEndRef.current,
        force,
      };
      if (!shouldFire(fireArgs)) return;
      // Write FIRST so a race (focus event landing simultaneously with a
      // 60s tick, or a fast reload) cannot double-fire.
      const today = getLocalDateString(now);
      writeState({ lastFiredLocalDate: today });
      onFireRef.current();
    };
    // Initial tick is delayed slightly so any pending UI (e.g. an onboarding
    // ack landing 400ms after the final chip) lands first.
    const initialTimeout = window.setTimeout(tick, INITIAL_TICK_DELAY_MS);
    const interval = window.setInterval(tick, CHECK_INTERVAL_MS);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearTimeout(initialTimeout);
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);
}
